import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Building2, Globe, Linkedin, Users2, Briefcase, Tag } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import NotesTimeline from "../components/NotesTimeline";
import HistoryTimeline from "../components/HistoryTimeline";
import CustomFieldsRenderer from "../components/CustomFieldsRenderer";
import ClientWidget from "../components/ClientWidget";
import DetailsCard from "../components/DetailsCard";
import { useSettings, formatMoney } from "../lib/settings";

const INDUSTRIES = [
  "Technology", "Finance", "Healthcare", "Real Estate", "Retail",
  "Education", "Legal", "Construction", "Marketing", "Other",
];
const EMPLOYEE_BANDS = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"];

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currency } = useSettings();
  const [company, setCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [users, setUsers] = useState([]);

  const load = async () => {
    try {
      const [c, ct, dl, p, u] = await Promise.all([
        api.get(`/companies/${id}`),
        api.get(`/companies/${id}/contacts`),
        api.get(`/companies/${id}/deals`),
        api.get("/pipelines"),
        api.get("/users"),
      ]);
      setCompany(c.data); setContacts(ct.data); setDeals(dl.data); setPipelines(p.data); setUsers(u.data);
    } catch { toast.error("Company not found"); navigate("/companies"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patch = async (data) => {
    try {
      const { data: updated } = await api.patch(`/companies/${id}`, data);
      setCompany(updated); toast.success("Saved");
      // reload domain-dependent lists
      const ct = await api.get(`/companies/${id}/contacts`);
      setContacts(ct.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const saveCustomFields = async (next) => {
    await patch({ custom_fields: next });
  };

  const stageName = (deal) => {
    const p = pipelines.find(x => x.id === deal.pipeline_id);
    return p?.stages.find(s => s.id === deal.stage_id)?.name || "—";
  };

  const ownerName = (uid) => users.find(u => u.id === uid)?.name || "—";

  if (!company) return <div className="px-8 py-12 text-mute mono text-xs">loading…</div>;

  return (
    <div data-testid="company-detail-page">
      <div className="px-8 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--pg-border)" }}>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/companies")} className="pg-btn pg-btn-ghost"><ArrowLeft size={14} /> Back</button>
          <div>
            <h1 className="text-2xl" style={{ fontWeight: 600 }} data-testid="company-name">{company.name}</h1>
            <div className="mono text-[11px] mt-1 flex items-center gap-2 flex-wrap">
              {company.domain && (
                <a href={`https://${company.domain}`} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1"
                   style={{ color: "var(--pg-accent-deep)" }}>
                  {company.domain} <ExternalLink size={11} />
                </a>
              )}
              {company.industry && <span className="pg-badge pg-badge-grey">{company.industry}</span>}
              <span className="text-mute">·</span>
              <span className="text-mute">{company.contact_count} contacts · {company.active_deal_count} active deals · <span style={{ color: "var(--pg-accent-deep)", fontWeight: 600 }}>{formatMoney(company.pipeline_value, currency.symbol)}</span></span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6 p-8">
        <div className="col-span-2 space-y-6">
          <ClientWidget companyId={company.id} />

          <CustomFieldsRenderer entity="company" values={company.custom_fields || {}} onSave={saveCustomFields} />

          {/* Contacts */}
          <div className="pg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm mono uppercase text-mute tracking-wider">Contacts</h3>
              <span className="mono text-[11px] text-mute">domain-driven · auto-matched</span>
            </div>
            {contacts.length === 0 ? (
              <div className="text-mute mono text-xs">No contacts share this domain yet</div>
            ) : (
              <table className="pg-table" style={{ width: "100%" }}>
                <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Owner</th><th>Last Activity</th></tr></thead>
                <tbody>
                  {contacts.map(c => (
                    <tr key={c.id} onClick={() => navigate(`/contacts/${c.id}`)} data-testid={`company-contact-${c.id}`}>
                      <td style={{ fontWeight: 600, color: "var(--pg-text)" }}>{c.name}</td>
                      <td className="mono text-xs">{c.email || "—"}</td>
                      <td className="mono text-xs">{c.phone || "—"}</td>
                      <td>{ownerName(c.owner_id)}</td>
                      <td className="mono text-xs">{(c.updated_at || c.created_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Deals — Associated cards style with purple border */}
          <div className="pg-card p-6" data-testid="associated-deals">
            <h3 className="text-sm mono uppercase text-mute tracking-wider mb-4">Associated Deals</h3>
            {deals.length === 0 ? (
              <div className="text-mute mono text-xs">No deals linked yet</div>
            ) : (
              <div className="space-y-2">
                {deals.map((d) => {
                  const stage = stageName(d);
                  return (
                    <Link
                      key={d.id}
                      to={`/deals/${d.id}`}
                      className="block pg-card p-3"
                      data-testid={`company-deal-${d.id}`}
                      style={{
                        background: "var(--pg-surface-2)",
                        borderColor: "#a855f7",
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div style={{ color: "var(--pg-text)" }}>{d.name}</div>
                          <div className="mono text-[11px] text-mute mt-0.5">
                            {stage} · {formatMoney(d.value, currency.symbol)} · {d.probability}%
                          </div>
                        </div>
                        <span className="mono text-[11px] text-mute">{d.status}</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          <NotesTimeline linkedType="company" linkedId={company.id} />
          <HistoryTimeline entityType="company" entityId={company.id} source={`/companies/${company.id}/history`} />
        </div>

        <div className="space-y-6">
          <DetailsCard
            testId="company-details-card"
            title="Company Information"
            createdAt={company.created_at}
            updatedAt={company.updated_at}
            onSave={patch}
            fields={[
              { key: "name",         label: "Name",      value: company.name,         icon: Building2 },
              { key: "domain",       label: "Domain",    value: company.domain,       icon: Globe },
              { key: "website",      label: "Website",   value: company.website,      icon: Globe,     type: "url" },
              { key: "linkedin_url", label: "LinkedIn",  value: company.linkedin_url, icon: Linkedin,  type: "url" },
              { key: "industry",     label: "Industry",  value: company.industry,     icon: Briefcase, type: "select",
                options: [{ value: "", label: "—" }, ...INDUSTRIES.map(i => ({ value: i, label: i }))] },
              { key: "employees",    label: "Employees", value: company.employees,    icon: Users2,    type: "select",
                options: [{ value: "", label: "—" }, ...EMPLOYEE_BANDS.map(e => ({ value: e, label: e }))] },
              { key: "source",       label: "Source",    value: company.source,       icon: Tag },
            ]}
          />

          <div className="pg-card p-6">
            <h3 className="text-sm mono uppercase text-mute tracking-wider mb-3">Quick Links</h3>
            <div className="space-y-2 text-sm">
              {company.website && (
                <a href={company.website} target="_blank" rel="noreferrer" className="flex items-center gap-2"
                   style={{ color: "var(--pg-accent-deep)" }}>
                  <Globe size={14} /> Website
                </a>
              )}
              {company.linkedin_url && (
                <a href={company.linkedin_url} target="_blank" rel="noreferrer" className="flex items-center gap-2"
                   style={{ color: "var(--pg-accent-deep)" }}>
                  <Linkedin size={14} /> LinkedIn
                </a>
              )}
              {!company.website && !company.linkedin_url && (
                <div className="text-mute mono text-xs">Add a website or LinkedIn URL above</div>
              )}
              {company.from_lead_id && (
                <Link to={`/leads/${company.from_lead_id}`} className="flex items-center gap-2"
                   style={{ color: "var(--pg-accent-deep)" }}>
                  <ExternalLink size={14} /> Original lead
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
